import { ImportantIcon, MandatoryIcon, OptimizingIcon } from '@bp/ui-components';
import { ComponentType } from 'react';
import { ConstraintPriorities } from '../../components/Constraints/types';
import { WarningPriority } from './types';

/**
 * Canonical severity model — single source of truth for how a finding's severity maps to
 * colour, icon, sort order and label. See docs/konzept-solver-rund-machen.md §1:
 *   error   = verhindert das Solven (harter Blocker)
 *   warning = führt vermutlich zu schlechten Plänen
 *   info    = optionale Optimierung
 */
export type SeverityLevel = 'error' | 'warning' | 'info';

/** Pre-flight / control warnings (WarningPriority). Missing priority is treated as a warning. */
export const priorityToSeverity = (priority?: WarningPriority): SeverityLevel => {
  if (priority === 'MANDATORY') return 'error';
  if (priority === 'OPTIMIZING') return 'info';
  return 'warning';
};

/** Per-resource constraint priority. Tolerates both the canonical upper-case and legacy lower-case. */
export const constraintPriorityToSeverity = (priority?: ConstraintPriorities | string | null): SeverityLevel => {
  const p = priority?.toUpperCase();
  if (p === 'MANDATORY') return 'error';
  if (p === 'OPTIMIZING') return 'info';
  return 'warning';
};

/** Timefold score tiers. */
export const scoreTierToSeverity = (tier: 'hard' | 'medium' | 'soft'): SeverityLevel => {
  if (tier === 'hard') return 'error';
  if (tier === 'soft') return 'info';
  return 'warning';
};

export type SeverityToken = {
  level: SeverityLevel;
  /** CSS custom property reference, e.g. `var(--color-error)`. */
  color: string;
  Icon: ComponentType<{ className?: string; color?: string }>;
  /** Lower sorts first: error (0) before warning (1) before info (2). */
  rank: number;
  /** i18n key for the human-readable level label (`solver.severity.*`). */
  labelKey: string;
};

const TOKENS: Record<SeverityLevel, SeverityToken> = {
  error: {
    level: 'error',
    color: 'var(--color-error)',
    Icon: MandatoryIcon,
    rank: 0,
    labelKey: 'solver.severity.error',
  },
  warning: {
    level: 'warning',
    color: 'var(--color-warning)',
    Icon: ImportantIcon,
    rank: 1,
    labelKey: 'solver.severity.warning',
  },
  info: { level: 'info', color: 'var(--color-focus)', Icon: OptimizingIcon, rank: 2, labelKey: 'solver.severity.info' },
};

export const severityToken = (level: SeverityLevel): SeverityToken => TOKENS[level];

/** Highest severity present in a list of warnings, or null if the list is empty. */
export const highestSeverity = (priorities: (WarningPriority | undefined)[]): SeverityLevel | null => {
  if (priorities.length === 0) return null;
  if (priorities.some((p) => priorityToSeverity(p) === 'error')) return 'error';
  if (priorities.some((p) => priorityToSeverity(p) === 'warning')) return 'warning';
  return 'info';
};
